import { loadOutfitsList } from '../../../../js/utils/outfitMappings.js';
import { normalizeGenderForFilter } from '../../../../js/utils/genderUtils.js';
import { getNsfwEnabled } from '../../../../js/utils/userPrefs.js';
import { filterOutfitsForSfw, outfitIsHiddenInFilteredMode } from '../../../../js/utils/nsfwTags.js';
import { fetchCache } from '../../../utils/fetchCache.js';
import { WORKER_URL } from '../../../config.js';

let femaleOutfitCache = { data: null, currentLang: null };
let maleOutfitCache = { data: null, currentLang: null };

export const createFallbackOutfits = () => filterOutfitsForSfw([
    { id: 0, name: 'default outfit' },
    { id: 1, name: 'nude' }
]);

export const ensureBaseOutfits = (outfits, characterGender) => {
    if (!Array.isArray(outfits)) return [];

    const normalizedGender = characterGender === 'male' ? 'male' : 'female';
    const numericGender = normalizedGender === 'male' ? 1 : 0;
    const withBase = [...outfits];
    const hasOutfit = (id) => withBase.some(outfit => outfit?.id?.toString() === id.toString());
    const additions = [];

    if (!hasOutfit(1) && !outfitIsHiddenInFilteredMode({ id: 1, name: 'nude' })) {
        additions.push({ id: 1, name: 'nude', gender: normalizedGender, gender_id: numericGender });
    }

    if (!hasOutfit(0)) {
        additions.push({ id: 0, name: 'default outfit', gender: normalizedGender, gender_id: numericGender });
    }

    additions.reverse().forEach(outfit => withBase.unshift(outfit));

    return withBase;
};

export async function fetchOutfitTranslations(lang, characterGender = 'female') {
    const cache = characterGender === 'male' ? maleOutfitCache : femaleOutfitCache;
    const cacheKey = `${lang}_${characterGender}_${getNsfwEnabled() ? 'unrestricted' : 'filtered'}`;

    if (cache.data && cache.currentLang === cacheKey) {
        return cache.data;
    }

    try {
        const allOutfits = await loadOutfitsList(lang);
        const targetGender = normalizeGenderForFilter(characterGender) || 'female';
        const filtered = allOutfits.filter((outfit) => {
            const gender = normalizeGenderForFilter(outfit.gender);
            if (!gender || gender === 'all') return true;
            return gender === targetGender;
        });

        const data = ensureBaseOutfits(filtered, characterGender);

        cache.data = data;
        cache.currentLang = cacheKey;
        return data;
    } catch (error) {
        console.error(`Error fetching ${characterGender} outfit translations for ${lang}:`, error);
        const fallback = ensureBaseOutfits(createFallbackOutfits(), characterGender);
        if (lang === 'en') {
            cache.data = fallback;
            cache.currentLang = cacheKey;
        }
        return fallback;
    }
}

export async function preloadAllOutfits(lang) {
    const [femaleOutfits, maleOutfits] = await Promise.all([
        fetchOutfitTranslations(lang, 'female'),
        fetchOutfitTranslations(lang, 'male')
    ]);
    return { femaleOutfits, maleOutfits };
}

export async function loadOutfits(lang, characterGender = 'female') {
    const data = await fetchOutfitTranslations(lang, characterGender);

    if (!data || !Array.isArray(data)) {
        console.error(`Settings Modal: Invalid data from fetchOutfitTranslations:`, data);
        return { outfits: [] };
    }

    return { outfits: data };
}

export async function fetchCharacterOutfitIds(characterId) {
    if (!characterId) return null;
    try {
        const ids = await fetchCache.fetchJSON(
            `${WORKER_URL}/character/outfits?character_id=${encodeURIComponent(characterId)}`
        );
        if (!Array.isArray(ids)) return null;
        return new Set(ids.map((id) => String(id)));
    } catch {
        return null;
    }
}

export const isOutfitUnavailableForCharacter = (outfitId, characterOutfitIds) => {
    if (!characterOutfitIds) return false;
    const id = String(outfitId);
    if (id === '0') return false;
    return !characterOutfitIds.has(id);
};
